'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { type Address, ChainUtils } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { InterfaceChainUtils, parseApiAsset, toBigInt } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { NetworkAvatar } from '@morpho-org/ui-v6/client';
import { V1Badge } from '@morpho-org/ui-v6/server';
import { formatters, getFirstAndRest } from '@morpho-org/ui-v6/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import { useId } from 'react';
import { ButtonLink } from '@/components/common/ButtonLink';
import { CollateralExposureStack } from '@/components/common/CollateralExposureStack';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { InfoHoverCardTrigger } from '@/components/common/InfoHoverCardTrigger';
import MerklRewardsHoverCard from '@/components/common/RewardsHoverCard/MerklRewardsHoverCard';
import { TokenWithHoverCard } from '@/components/common/TokenWithHoverCard';
import { WarningIcon } from '@/components/common/WarningIcon';
import { aggregateVaultV1Warnings } from '@/helpers/aggregateVaultV1Warnings';
import { getCollateralExposures } from '@/helpers/collateralExposures';
import { getParsedValue } from '@/helpers/formatters';
import { splitValueAndUnit } from '@/helpers/number';
import { normalizeVaultApyRewards } from '@/helpers/rewards';
import { getCuratorUrl } from '@/helpers/routing';
import { getWarningsColor } from '@/helpers/warnings';
import { useVaultV1Apy } from '@/hooks/useVaultV1Apy';
import { trackMetricTooltipOpenChange } from '@/lib/analytics/client/trackers/metric-tooltip';
import type { VaultHeroData } from './VaultHero.mapper';
import { LinkifiedVaultDescription } from '../../components/VaultHero/LinkifiedVaultDescription';
import { ResponsiveVaultTitle } from '../../components/VaultHero/ResponsiveVaultTitle';
import { VaultV1LiquidityHoverCard } from './VaultV1LiquidityHoverCard';
export interface VaultHeroClientProps {
  address: Address;
  vault: VaultHeroData;
  pendingActionsSlot?: ReactNode;
}
const VaultHeroClient = t0 => {
  const $ = _c(166);
  const {
    address,
    vault,
    pendingActionsSlot
  } = t0;
  const chainId = vault.chainId;
  let aggregatedWarnings;
  let asset;
  let formattedTitle;
  let formattedTotalDeposits;
  let t1;
  let totalDepositsUsd;
  if ($[0] !== vault.asset || $[1] !== vault.name || $[2] !== vault.state.assetDecimals || $[3] !== vault.state.totalAssets || $[4] !== vault.state.totalAssetsUsd || $[5] !== vault.warnings) {
    aggregatedWarnings = aggregateVaultV1Warnings(vault.warnings);
    let t2;
    if ($[12] !== vault.asset || $[13] !== vault.state.assetDecimals || $[14] !== vault.state.totalAssets || $[15] !== vault.state.totalAssetsUsd) {
      asset = getParsedValue(vault.asset, parseApiAsset);
      let t3;
      if ($[19] !== vault.state.totalAssetsUsd) {
        bb0: {
          const value = getParsedValue(vault.state.totalAssetsUsd, safeParseNumber);
          if (!isDefined(value)) {
            t3 = null;
            break bb0;
          }
          const formattedValue = formatters.short.digits(2).trailingZero(false).default("-").of(value, 18);
          t3 = splitValueAndUnit(formattedValue);
        }
        $[19] = vault.state.totalAssetsUsd;
        $[20] = t3;
      } else {
        t3 = $[20];
      }
      totalDepositsUsd = t3;
      t2 = formatters.short.digits(2).trailingZero(false).default("-").unit(asset?.symbol).of(toBigInt(vault.state.totalAssets), vault.state.assetDecimals);
      $[12] = vault.asset;
      $[13] = vault.state.assetDecimals;
      $[14] = vault.state.totalAssets;
      $[15] = vault.state.totalAssetsUsd;
      $[16] = asset;
      $[17] = t2;
      $[18] = totalDepositsUsd;
    } else {
      asset = $[16];
      t2 = $[17];
      totalDepositsUsd = $[18];
    }
    formattedTotalDeposits = t2;
    const t3 = vault.name ?? "";
    let t4;
    if ($[21] !== t3) {
      t4 = getFirstAndRest(t3);
      $[21] = t3;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    formattedTitle = t4;
    t1 = getWarningsColor(aggregatedWarnings);
    $[0] = vault.asset;
    $[1] = vault.name;
    $[2] = vault.state.assetDecimals;
    $[3] = vault.state.totalAssets;
    $[4] = vault.state.totalAssetsUsd;
    $[5] = vault.warnings;
    $[6] = aggregatedWarnings;
    $[7] = asset;
    $[8] = formattedTitle;
    $[9] = formattedTotalDeposits;
    $[10] = t1;
    $[11] = totalDepositsUsd;
  } else {
    aggregatedWarnings = $[6];
    asset = $[7];
    formattedTitle = $[8];
    formattedTotalDeposits = $[9];
    t1 = $[10];
    totalDepositsUsd = $[11];
  }
  const titleColor = t1;
  const t2 = vault.asset;
  let t3;
  if ($[23] !== vault.allocation || $[24] !== vault.fee || $[25] !== vault.state.netApy || $[26] !== vault.state.netApyExcludingRewards) {
    t3 = {
      fee: vault.fee,
      netApyExcludingRewards: vault.state.netApyExcludingRewards,
      netApy: vault.state.netApy,
      allocation: vault.allocation
    };
    $[23] = vault.allocation;
    $[24] = vault.fee;
    $[25] = vault.state.netApy;
    $[26] = vault.state.netApyExcludingRewards;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  let t4;
  if ($[28] !== vault.rewards) {
    t4 = normalizeVaultApyRewards(vault.rewards);
    $[28] = vault.rewards;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  let t5;
  if ($[30] !== address || $[31] !== t3 || $[32] !== t4 || $[33] !== vault.asset) {
    t5 = {
      vaultV1: {
        address,
        asset: t2,
        state: t3,
        rewards: t4
      }
    };
    $[30] = address;
    $[31] = t3;
    $[32] = t4;
    $[33] = vault.asset;
    $[34] = t5;
  } else {
    t5 = $[34];
  }
  const {
    apy,
    netApy,
    points,
    rewards
  } = useVaultV1Apy(t5);
  let t6;
  if ($[35] !== netApy) {
    t6 = formatters.percent.digits(2).default("-").max(10).of(netApy, 18);
    $[35] = netApy;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  const formattedNetApy = t6;
  let t7;
  if ($[37] !== chainId) {
    t7 = InterfaceChainUtils.getMetadata(chainId);
    $[37] = chainId;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  const chain = t7;
  const netApyLabelId = useId();
  let t8;
  if ($[39] !== vault.allocation) {
    t8 = getCollateralExposures(vault.allocation);
    $[39] = vault.allocation;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  const collateralExposures = t8;
  let t9;
  if ($[41] !== vault.state.totalAssetsUsd) {
    t9 = getParsedValue(vault.state.totalAssetsUsd, safeParseNumber);
    $[41] = vault.state.totalAssetsUsd;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  const totalAssetsUsd = t9;
  let t10;
  if ($[43] !== address || $[44] !== aggregatedWarnings || $[45] !== formattedTitle || $[46] !== titleColor || $[47] !== vault.name) {
    t10 = vault.name ? <div className="max-md:gap-xs flex flex-wrap gap-[24px]"><h1 className="max-md:gap-xs flex flex-wrap gap-[24px]"><ResponsiveVaultTitle color={titleColor}>{formattedTitle.first}</ResponsiveVaultTitle><ResponsiveVaultTitle color={titleColor || "text.tertiary"}>{formattedTitle.rest}</ResponsiveVaultTitle></h1><WarningIcon warnings={aggregatedWarnings} iconSize={50} /></div> : <div className="gap-sm flex flex-wrap items-center"><h1 className="flex flex-wrap items-center"><ResponsiveVaultTitle color={titleColor}>{abbreviateAddress(address)}</ResponsiveVaultTitle></h1><WarningIcon warnings={aggregatedWarnings} iconSize={50} /></div>;
    $[43] = address;
    $[44] = aggregatedWarnings;
    $[45] = formattedTitle;
    $[46] = titleColor;
    $[47] = vault.name;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <V1Badge size="l" />;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== t10) {
    t12 = <div className="gap-lg flex items-center"><div className="gap-sm flex items-center">{t10}{t11}</div></div>;
    $[50] = t10;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== pendingActionsSlot) {
    t13 = <div key="pending-actions" className="max-md:hidden">{pendingActionsSlot}</div>;
    $[52] = pendingActionsSlot;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== t12 || $[55] !== t13) {
    t14 = <div className="gap-lg flex w-[100%] flex-row items-center justify-between">{t12}{t13}</div>;
    $[54] = t12;
    $[55] = t13;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== address || $[58] !== chainId) {
    t15 = ChainUtils.getExplorerAddressUrl(chainId, address);
    $[57] = address;
    $[58] = chainId;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  let t16;
  if ($[60] !== address) {
    t16 = abbreviateAddress(address);
    $[60] = address;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== t16) {
    t17 = {
      variant: "ghost",
      label: t16,
      size: "default"
    };
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t15 || $[65] !== t17) {
    t18 = <TooltipTrigger asChild={true}><span><ButtonLink href={t15} mode="external" buttonProps={t17} /></span></TooltipTrigger>;
    $[64] = t15;
    $[65] = t17;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <TooltipContent>View on chain explorer</TooltipContent>;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t18) {
    t20 = <Tooltip>{t18}{t19}</Tooltip>;
    $[68] = t18;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== chainId) {
    t21 = <NetworkAvatar chainId={chainId} />;
    $[70] = chainId;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== chain.chain.name) {
    t22 = <span className="text-2xs text-foreground">{chain.chain.name}</span>;
    $[72] = chain.chain.name;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== t21 || $[75] !== t22) {
    t23 = <div className="gap-2xs flex items-center">{t21}{t22}</div>;
    $[74] = t21;
    $[75] = t22;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  let t24;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      flexWrap: "wrap"
    };
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] !== vault.curators) {
    t25 = vault.curators.map(_temp);
    $[78] = vault.curators;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== vault.curatorsPartial) {
    t26 = vault.curatorsPartial && <span className="text-secondary-foreground text-xs" data-testid="vault-hero-curators-partial">Some curators could not be loaded, so this list may be incomplete.</span>;
    $[80] = vault.curatorsPartial;
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  let t27;
  if ($[82] !== t25 || $[83] !== t26) {
    t27 = <div className="gap-xs flex items-center" style={t24}>{t25}{t26}</div>;
    $[82] = t25;
    $[83] = t26;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  let t28;
  if ($[85] !== asset) {
    t28 = asset && <TokenWithHoverCard asset={asset} />;
    $[85] = asset;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  let t29;
  if ($[87] !== pendingActionsSlot) {
    t29 = <div key="pending-actions" className="md:hidden">{pendingActionsSlot}</div>;
    $[87] = pendingActionsSlot;
    $[88] = t29;
  } else {
    t29 = $[88];
  }
  let t30;
  if ($[89] !== t20 || $[90] !== t23 || $[91] !== t27 || $[92] !== t28 || $[93] !== t29) {
    t30 = <div className="gap-sm flex flex-wrap items-center">{t20}{t23}{t27}{t28}{t29}</div>;
    $[89] = t20;
    $[90] = t23;
    $[91] = t27;
    $[92] = t28;
    $[93] = t29;
    $[94] = t30;
  } else {
    t30 = $[94];
  }
  let t31;
  if ($[95] !== t14 || $[96] !== t30) {
    t31 = <div className="gap-sm flex w-full flex-col">{t14}{t30}</div>;
    $[95] = t14;
    $[96] = t30;
    $[97] = t31;
  } else {
    t31 = $[97];
  }
  let t32;
  if ($[98] !== vault.metadata) {
    t32 = vault.metadata?.description && <LinkifiedVaultDescription description={vault.metadata.description} />;
    $[98] = vault.metadata;
    $[99] = t32;
  } else {
    t32 = $[99];
  }
  let t33;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="flex items-center"><span className="text-secondary-foreground text-xs" style={{
        whiteSpace: "nowrap"
      }}>Total Deposits</span></div>;
    $[100] = t33;
  } else {
    t33 = $[100];
  }
  const t34 = totalDepositsUsd ? `$${totalDepositsUsd.value}` : "-";
  let t35;
  if ($[101] !== t34) {
    t35 = <span className="text-title-sm">{t34}</span>;
    $[101] = t34;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  const t36 = totalDepositsUsd?.unit;
  let t37;
  if ($[103] !== t36) {
    t37 = <span className="text-title-sm text-muted-foreground">{t36}</span>;
    $[103] = t36;
    $[104] = t37;
  } else {
    t37 = $[104];
  }
  let t38;
  if ($[105] !== t35 || $[106] !== t37) {
    t38 = <div className="flex items-center">{t35}{t37}</div>;
    $[105] = t35;
    $[106] = t37;
    $[107] = t38;
  } else {
    t38 = $[107];
  }
  let t39;
  if ($[108] !== formattedTotalDeposits) {
    t39 = <span className="text-xs">{formattedTotalDeposits}</span>;
    $[108] = formattedTotalDeposits;
    $[109] = t39;
  } else {
    t39 = $[109];
  }
  let t40;
  if ($[110] !== t38 || $[111] !== t39) {
    t40 = <div className="gap-sm flex flex-1 flex-col">{t33}{t38}{t39}</div>;
    $[110] = t38;
    $[111] = t39;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  let t41;
  if ($[113] !== asset || $[114] !== vault) {
    t41 = <VaultV1LiquidityHoverCard vault={vault} asset={asset} />;
    $[113] = asset;
    $[114] = vault;
    $[115] = t41;
  } else {
    t41 = $[115];
  }
  let t42;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className="flex items-center gap-[6px]"><span className="text-secondary-foreground text-xs" style={{
        whiteSpace: "nowrap"
      }}>Exposure</span></div>;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  let t43;
  if ($[117] !== collateralExposures || $[118] !== totalAssetsUsd) {
    t43 = <div className="gap-sm flex flex-1 flex-col">{t42}<CollateralExposureStack collateralExposures={collateralExposures} totalAssetsUsd={totalAssetsUsd} maxTokensCount={5} size="regular" /></div>;
    $[117] = collateralExposures;
    $[118] = totalAssetsUsd;
    $[119] = t43;
  } else {
    t43 = $[119];
  }
  let t44;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      whiteSpace: "nowrap"
    };
    $[120] = t44;
  } else {
    t44 = $[120];
  }
  let t45;
  if ($[121] !== netApyLabelId) {
    t45 = <span id={netApyLabelId} className="text-secondary-foreground text-xs" style={t44}>Net APY</span>;
    $[121] = netApyLabelId;
    $[122] = t45;
  } else {
    t45 = $[122];
  }
  let t46;
  if ($[123] !== vault.fee) {
    t46 = getParsedValue(vault.fee, safeParseNumber);
    $[123] = vault.fee;
    $[124] = t46;
  } else {
    t46 = $[124];
  }
  let t47;
  if ($[125] !== apy || $[126] !== asset || $[127] !== netApy || $[128] !== points || $[129] !== rewards || $[130] !== t46) {
    t47 = <InfoHoverCardTrigger onOpenChange={_temp2}><MerklRewardsHoverCard performanceFee={t46} apy={apy} rewards={rewards} points={points} variant="vault" netApy={netApy} asset={asset} /></InfoHoverCardTrigger>;
    $[125] = apy;
    $[126] = asset;
    $[127] = netApy;
    $[128] = points;
    $[129] = rewards;
    $[130] = t46;
    $[131] = t47;
  } else {
    t47 = $[131];
  }
  let t48;
  if ($[132] !== t45 || $[133] !== t47) {
    t48 = <div className="gap-2xs flex items-center">{t45}{t47}</div>;
    $[132] = t45;
    $[133] = t47;
    $[134] = t48;
  } else {
    t48 = $[134];
  }
  let t49;
  if ($[135] !== vault.fee) {
    t49 = getParsedValue(vault.fee, safeParseNumber);
    $[135] = vault.fee;
    $[136] = t49;
  } else {
    t49 = $[136];
  }
  let t50;
  if ($[137] !== apy || $[138] !== asset || $[139] !== netApy || $[140] !== points || $[141] !== rewards || $[142] !== t49) {
    t50 = {
      performanceFee: t49,
      apy,
      rewards,
      points,
      variant: "vault",
      netApy,
      asset
    };
    $[137] = apy;
    $[138] = asset;
    $[139] = netApy;
    $[140] = points;
    $[141] = rewards;
    $[142] = t49;
    $[143] = t50;
  } else {
    t50 = $[143];
  }
  let t51;
  if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = {
      whiteSpace: "nowrap"
    };
    $[144] = t51;
  } else {
    t51 = $[144];
  }
  let t52;
  if ($[145] !== formattedNetApy) {
    t52 = <span data-testid="vault-v1-hero-net-apy" className="text-title-sm">{formattedNetApy}</span>;
    $[145] = formattedNetApy;
    $[146] = t52;
  } else {
    t52 = $[146];
  }
  let t53;
  if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <span className="gap-2xs flex items-center"><span className="text-title-sm text-muted-foreground">%</span></span>;
    $[147] = t53;
  } else {
    t53 = $[147];
  }
  let t54;
  if ($[148] !== t52) {
    t54 = <span className="flex" style={t51}>{t52}{t53}</span>;
    $[148] = t52;
    $[149] = t54;
  } else {
    t54 = $[149];
  }
  let t55;
  if ($[150] !== netApyLabelId || $[151] !== t50 || $[152] !== t54) {
    t55 = <IconRewardHover labelledBy={netApyLabelId} RewardsHoverCardProps={t50} iconSize={35}>{t54}</IconRewardHover>;
    $[150] = netApyLabelId;
    $[151] = t50;
    $[152] = t54;
    $[153] = t55;
  } else {
    t55 = $[153];
  }
  let t56;
  if ($[154] !== t48 || $[155] !== t55) {
    t56 = <div className="gap-sm flex flex-1 flex-col">{t48}{t55}</div>;
    $[154] = t48;
    $[155] = t55;
    $[156] = t56;
  } else {
    t56 = $[156];
  }
  let t57;
  if ($[157] !== t40 || $[158] !== t41 || $[159] !== t43 || $[160] !== t56) {
    t57 = <div className="gap-lg flex w-full flex-col justify-start md:flex-row md:flex-wrap">{t40}{t41}{t43}{t56}</div>;
    $[157] = t40;
    $[158] = t41;
    $[159] = t43;
    $[160] = t56;
    $[161] = t57;
  } else {
    t57 = $[161];
  }
  let t58;
  if ($[162] !== t31 || $[163] !== t32 || $[164] !== t57) {
    t58 = <div data-testid="vault-hero" className="flex w-full flex-col items-start gap-[48px]">{t31}{t32}{t57}</div>;
    $[162] = t31;
    $[163] = t32;
    $[164] = t57;
    $[165] = t58;
  } else {
    t58 = $[165];
  }
  return t58;
};
export default VaultHeroClient;
function _temp(curator) {
  return <div className="gap-xs max-md:gap-2xs flex items-center" key={curator.id}><ButtonLink href={getCuratorUrl({
      curatorId: curator.id
    })} mode="internal" buttonProps={{
      variant: "ghost",
      label: curator.name,
      size: "default",
      avatarProps: {
        src: curator.image
      }
    }} /></div>;
}
function _temp2(isOpen) {
  return trackMetricTooltipOpenChange(isOpen, {
    page: "vault",
    metric: "net_apy",
    vault_version: "v1"
  });
}